import { useLocation, useNavigate, Outlet, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  useAppSelector,
  useQuery,
  useAppDispatch,
  useLazyRegionLoginQuery,
  useGetProductVersionsQuery,
  parseRedirectAccountFromHkey,
  getLocalSelectedOU,
  deleteLocalSelectedOU,
  getLocalRetentionUser
} from '@monorepo/shared';
import { Loader, Message, useToaster } from 'rsuite';
import { server } from '../config/const';
import { selectOU } from '@monorepo/shared/store/fcOuSlice';
import {
  selectAccounts,
  selectCurrentAccount,
  updateRedirectAccount
} from '@monorepo/shared/store/accountSlice';
import {
  selectPermission,
  selectRegions,
  selectRejectedRegions,
  updateDebugKey,
  updateNeedLoginRegions
} from '@monorepo/shared/store/authSlice';
import { t } from 'i18next';
import { selectGroupRegions } from '@monorepo/shared/store/groupSlice';

export const PrivateRoute = () => {
  let query = useQuery();
  const { accountId } = useParams();
  const { pathname, state } = useLocation();
  const debug_key = query.get('debug_key') || state?.debug_key;
  const redirect_data = query.get('redirect_data') || '';
  const obj = parseRedirectAccountFromHkey(redirect_data);
  // deleted local cached selected account if redirect from FC
  if (redirect_data) deleteLocalSelectedOU();
  // Note: redirect_account, redirect_user, access_from, org_id is from VM redirect
  const redirect_account =
    query.get('redirect_account') || state?.redirect_account;
  const access_from = query.get('access_from') || state?.access_from;
  const org_id = query.get('org_id') || state?.org_id;

  const redirect_obj =
    obj?.account_id || redirect_account
      ? {
          account_id: obj?.account_id || redirect_account,
          user_id: obj?.user_id,
          node_id: obj?.node_id,
          org_id: org_id,
          access_from: {
            user_id: obj?.by_user_id || access_from,
            account_id: obj?.by_account_id
          }
        }
      : state?.redirect_obj || {};

  updateRedirectAccount(redirect_obj);

  const data = query.get('data') || state?.data;
  const user = query.get('user') || state?.user || getLocalRetentionUser();
  const dispatch = useAppDispatch();
  const localSelectedOU = getLocalSelectedOU();
  const userPermission = useAppSelector(selectPermission);
  const ouData = useAppSelector(selectOU);
  const accounts = useAppSelector(selectAccounts);
  const regions = useAppSelector(selectRegions);
  const rejectedRegions = useAppSelector(selectRejectedRegions);
  const groupRegions = useAppSelector(selectGroupRegions);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const navigate = useNavigate();
  const [regionLogin] = useLazyRegionLoginQuery();
  const toaster = useToaster();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const productVersions = useGetProductVersionsQuery(
    { server: server },
    {
      skip: userPermission === 'unset'
    }
  );

  useEffect(() => {
    if (debug_key) {
      dispatch(updateDebugKey(debug_key));
    }
  }, [debug_key]);

  useEffect(() => {
    if (Object.keys(rejectedRegions).length) {
      const keys = Object.keys(rejectedRegions);
      const index = keys.length - 1;
      const regionErrMessage = (
        <Message
          showIcon
          type="warning"
          closable
          header={t('common.apiMsg.warning.region')}
        >
          <span>
            <b>{`${rejectedRegions[keys[index]]?.name}`}</b>
          </span>
        </Message>
      );
      toaster.push(regionErrMessage, {
        placement: 'bottomEnd',
        duration: 5000
      });
    }
  }, [rejectedRegions]);

  useEffect(() => {
    if (userPermission === 'unset') {
      const redirectPath =
        accountId && pathname.includes(`/organization/${accountId}`)
          ? `/organization/${accountId}`
          : pathname.includes('/group/accounts')
          ? '/group/accounts'
          : pathname;
      navigate('/permission', {
        state: {
          redirect_path: redirectPath,
          redirect_obj,
          debug_key,
          data,
          user
        }
      });
    } else if (
      userPermission === 'passed' &&
      !redirect_obj?.account_id &&
      !user
    ) {
      if (!pathname.includes('cred') && !localSelectedOU?.idPath) {
        navigate(`/cred/ou`, {
          state: {
            debug_key
          }
        });
      }
    }
    setIsLoading(false);
  }, [userPermission, currentAccount, ouData]);

  useEffect(() => {
    const loginRegions = (regions: Record<string, any>[]) => {
      regions?.forEach((region: Record<string, any>) => {
        if (region.globalToken && !region.token) {
          regionLogin({ region: region, disableTimezone: true });
        }
      });
    };
    if (userPermission === 'passed' && Object.keys(regions).length) {
      const accountArr = Object.values(accounts);
      const regionArr = Object.values(regions);
      // only master user can provision instance
      const masterUser = accountArr?.find(
        (account: Record<string, any>) => account.user_type === 'Master'
      );
      const accountsRegions =
        accountArr
          ?.filter((account: Record<string, any>) => account.region)
          .map((account: Record<string, any>) => account.region) || [];
      // Currently load all regions for FSR
      loginRegions(regionArr);
      dispatch(updateNeedLoginRegions(regions));
    }
  }, [regions, userPermission, groupRegions]);

  return (
    <>
      {isLoading ? (
        <Loader center content={t('common.loading')} />
      ) : (
        <Outlet
          context={{
            redirect_obj: redirect_obj,
            data: data
          }}
        />
      )}
    </>
  );
};
